import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function UserInfoPage({ userInfo, setUserInfo, onNext }) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserInfo(prev => ({ ...prev, [name]: value }));

    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      // Validate with backend
      const response = await axios.post(`${API_URL}/api/validate-user-info`, userInfo);
      const validation = response.data;

      if (!validation.valid) {
        // Set field-specific errors
        setErrors(validation.errors || {});
        setLoading(false);

        // Scroll to first error
        const firstErrorField = Object.keys(validation.errors)[0];
        if (firstErrorField) {
          const element = document.getElementById(firstErrorField);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        }
        return;
      }

      // All valid, proceed to next step
      onNext();
    } catch (error) {
      console.error('Validation error:', error);

      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({
          _general: 'Validation failed. Please check your information and try again.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getInputClassName = (fieldName) => {
    const baseClasses = "w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary transition-colors";

    if (errors[fieldName]) {
      return `${baseClasses} border-error focus:border-error`;
    }

    return `${baseClasses} border-border focus:border-primary`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-border p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-navy mb-2">
          Step 1 of 4: Your Information
        </h2>
        <div className="w-32 h-1 bg-primary"></div>
      </div>

      {errors._general && (
        <div className="mb-6 p-4 bg-red-50 border border-error rounded-lg flex items-start space-x-3">
          <svg className="w-5 h-5 text-error mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-error text-sm">{errors._general}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Full Name */}
        <div>
          <label htmlFor="full_name" className="block text-sm font-medium text-navy mb-2">
            Full Name <span className="text-error">*</span>
          </label>
          <input
            type="text"
            id="full_name"
            name="full_name"
            required
            value={userInfo.full_name}
            onChange={handleChange}
            onBlur={() => handleBlur('full_name')}
            className={getInputClassName('full_name')}
            placeholder="John Smith"
            aria-invalid={errors.full_name ? 'true' : 'false'}
            aria-describedby={errors.full_name ? 'full_name_error' : undefined}
          />
          {errors.full_name && (
            <div id="full_name_error" className="mt-2 flex items-start space-x-2">
              <svg className="w-4 h-4 text-error mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-error">{errors.full_name}</p>
            </div>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-navy mb-2">
            Email Address <span className="text-error">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={userInfo.email}
            onChange={handleChange}
            onBlur={() => handleBlur('email')}
            className={getInputClassName('email')}
            placeholder="john.smith@example.com"
            aria-invalid={errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'email_error' : undefined}
          />
          {errors.email && (
            <div id="email_error" className="mt-2 flex items-start space-x-2">
              <svg className="w-4 h-4 text-error mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-error">{errors.email}</p>
            </div>
          )}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-navy mb-2">
            Phone Number <span className="text-error">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            value={userInfo.phone}
            onChange={handleChange}
            onBlur={() => handleBlur('phone')}
            className={getInputClassName('phone')}
            placeholder="+1-555-123-4567"
            aria-invalid={errors.phone ? 'true' : 'false'}
            aria-describedby={errors.phone ? 'phone_error' : undefined}
          />
          {errors.phone && (
            <div id="phone_error" className="mt-2 flex items-start space-x-2">
              <svg className="w-4 h-4 text-error mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-error">{errors.phone}</p>
            </div>
          )}
        </div>

        {/* Address */}
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-navy mb-2">
            Address <span className="text-error">*</span>
          </label>
          <input
            type="text"
            id="address"
            name="address"
            required
            value={userInfo.address}
            onChange={handleChange}
            onBlur={() => handleBlur('address')}
            className={getInputClassName('address')}
            placeholder="123 Main St, New York, NY 10001, USA"
            aria-invalid={errors.address ? 'true' : 'false'}
            aria-describedby={errors.address ? 'address_error' : undefined}
          />
          {errors.address && (
            <div id="address_error" className="mt-2 flex items-start space-x-2">
              <svg className="w-4 h-4 text-error mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-error">{errors.address}</p>
            </div>
          )}
        </div>

        {/* Interests */}
        <div>
          <label htmlFor="interests" className="block text-sm font-medium text-navy mb-2">
            Analysis Purpose <span className="text-error">*</span>
            <span className="text-text-secondary font-normal ml-2">(minimum 10 words)</span>
          </label>
          <textarea
            id="interests"
            name="interests"
            required
            rows={4}
            value={userInfo.interests}
            onChange={handleChange}
            onBlur={() => handleBlur('interests')}
            className={getInputClassName('interests')}
            placeholder="I want to analyze this company because I am considering investing in their stock and want to understand their financial health, growth prospects, and risk factors..."
            aria-invalid={errors.interests ? 'true' : 'false'}
            aria-describedby={errors.interests ? 'interests_error' : undefined}
          />
          {errors.interests && (
            <div id="interests_error" className="mt-2 flex items-start space-x-2">
              <svg className="w-4 h-4 text-error mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-error">{errors.interests}</p>
            </div>
          )}
          {!errors.interests && (
            <p className="mt-2 text-xs text-text-secondary">
              Please provide meaningful information about why you're analyzing these documents (minimum 10 words).
            </p>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={loading}
            className="px-8 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Validating...</span>
              </>
            ) : (
              <>
                <span>Continue</span>
                <span>→</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
